/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, ReactNode, useEffect, useMemo } from 'react';
import { PageonModal, ErrorHandlerAlert } from '../../molecules';
import { useHQVContext } from '../../context/HQVContext';
import { HQV_ACTIONS } from '../../reducers';
import { constants } from '../../constants';
import { eventUtil } from '@marriott/mi-ui-library';
import { useCheckBreakpoint } from '../../hooks/lib/useCheckBreakpoint';
import { getItemFromPageModel, getSessionIDFromCookie, respGridUtil, getMockPageModel } from '../../utils/src/helper';
import { PROPERTY_INFO_DATA } from '../../modules/constants';
import { useSearchResultsStore } from '../../modules/store/searchResultsStore';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

const { ROUTING_CONFIG, EXPERIENCE_SEGMENT, HQV_SEOURL, ITEM_PATH_ROOT, PAGE_NAME, SPA_APP_NAME } = constants;

export interface HQVWrapperProps {
  children: ReactNode;
  currentLocale: string;
  globalData?: any;
  model?: any;
}

export const HQVWrapper: FC<HQVWrapperProps> = ({ children, currentLocale, globalData, model }) => {
  // Get the full HQV context
  const { hqvDispatch, hqvState } = useHQVContext();

  const isViewportM = useCheckBreakpoint('viewportM');
  const setSearchResultsState = useSearchResultsStore(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: { setSearchResultsState: any }) => state.setSearchResultsState
  );
  const { envVarsObject } = useClientEnvVarsStore();
  // Get session token
  let sessionToken = '';
  if (globalData?.sessionData && (globalData?.sessionData?.data || globalData?.sessionData?.cacheData)) {
    const sessionObj = globalData?.sessionData?.data ? globalData?.sessionData : globalData?.sessionData?.cacheData;
    sessionToken = sessionObj?.data?.sessionToken || sessionObj?.key || getSessionIDFromCookie() || '';
  }

  // Request body for AEM template call in which SEOURL will decide which template to load
  const getCMSTemplateReqBody = useMemo(
    () => ({
      requestType: ROUTING_CONFIG,
      seoUrl: HQV_SEOURL,
      localeKey: currentLocale,
      experienceSegment: EXPERIENCE_SEGMENT,
      applicationName: SPA_APP_NAME,
      sessionToken: sessionToken,
    }),
    [currentLocale, sessionToken]
  );

  // function to close the modal and reset the loader code
  const closeModal = () => {
    //delete data for property info from store on close of HQV model
    setSearchResultsState([PROPERTY_INFO_DATA], {
      [PROPERTY_INFO_DATA]: { [PROPERTY_INFO_DATA]: {} },
    });
    const modalHeaderComp = document.getElementsByClassName('hqv-modal-content-header')[0] as HTMLElement;
    if (modalHeaderComp) modalHeaderComp.style.visibility = 'hidden';
    const loaderComp = document.getElementsByClassName('modal-content-loader')[0] as HTMLElement;
    if (loaderComp) {
      loaderComp.style.visibility = 'visible';
      loaderComp.style.display = 'block';
      if (!isViewportM) {
        loaderComp.style.display = 'flex';
      }
    }
    const modalDataComp = document.getElementsByClassName('hqv-modal-data')[0] as HTMLElement;
    if (modalDataComp) {
      modalDataComp.style.visibility = 'hidden';
      modalDataComp.style.display = 'none';
    }
    hqvDispatch({ type: HQV_ACTIONS.MODAL_OPEN_STATE, payload: false });
    document.body.style.height = '100%';
    if (sessionStorage.getItem('map_view') !== 'true') {
      document.body.style.overflow = 'auto';
    }
  };

  // function to hide the loader and show the actual content
  // using styling through JS so as to eliminate re-renders
  function showModalContent(flagLocation: string) {
    if (global && global.loggerInstance) {
      global.loggerInstance(`[hideOrShowModalContent] Flag triggered from ${flagLocation}`);
    }
    const modalHeaderComp = document.getElementsByClassName('hqv-modal-content-header')[0] as HTMLElement;
    if (modalHeaderComp) modalHeaderComp.style.visibility = 'visible';
    const loaderComp = document.getElementsByClassName('modal-content-loader')[0] as HTMLElement;
    if (loaderComp) {
      loaderComp.style.visibility = 'hidden';
      loaderComp.style.display = 'none';
    }
    const modalDataComp = document.getElementsByClassName('hqv-modal-data')[0] as HTMLElement;
    if (modalDataComp) {
      modalDataComp.style.visibility = 'visible';
      modalDataComp.style.display = 'block';
    }
    const modalCloseButton = document.getElementsByClassName('popup-close')[0] as HTMLElement;
    modalCloseButton?.focus();
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    eventUtil.remove('HQVComponentCallCompleted', () => {});
  }

  // PageonModal useEffect hooks
  useEffect(() => {
    if (model?.enableAdfSearchForm?.toString() === 'true') {
      const { AriesSearch, AriesReservation } = globalData?.sessionData?.cacheData?.data || {};
      hqvDispatch({
        type: HQV_ACTIONS.PROPERTY_IDENTITY,
        payload: AriesSearch?.searchCriteria?.propertyId || AriesReservation?.propertyId,
      });
    }
  }, []);

  useEffect(() => {
    eventUtil.on('showHotelDetailModal', () => {
      hqvDispatch({ type: HQV_ACTIONS.MODAL_OPEN_STATE, payload: true });
      document.body.style.overflow = 'hidden';
    });
  }, []);

  // useEffect to add an event listener for the custom event
  useEffect(() => {
    eventUtil.on(`HQVComponentCallCompleted`, data => {
      // Checking if the loader is already hidden. If yes, no need to trigger the same code again
      const loaderComp = document.getElementsByClassName('modal-content-loader')[0] as HTMLElement;
      if (loaderComp && loaderComp.style.visibility !== 'hidden') {
        showModalContent(data);
      }
    });
    return (): void => {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      eventUtil.remove('HQVComponentCallCompleted', () => {});
    };
  }, []);

  // Check if environment variables are loaded
  const hasRequiredEnvVars =
    envVarsObject?.['NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT'] && envVarsObject?.['NEXT_PUBLIC_AEM_PATH'];

  return (
    <>
      {children}
      {hasRequiredEnvVars && (
        <PageonModal
          templateURL={envVarsObject['NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT']}
          modelReqBody={getCMSTemplateReqBody}
          hotelQuickViewCacheConfig={globalData?.sessionData?.hotelQuickViewCacheConfig}
          modalOpenState={hqvState.modalOpenState}
          closeModal={closeModal}
          operationSignatures={globalData?.sessionData?.operationSignatures}
          enableApolloUtility={false}
          aemPath={envVarsObject['NEXT_PUBLIC_AEM_PATH']}
          itemPath={ITEM_PATH_ROOT}
          pageName={PAGE_NAME}
          getMockPageModel={getMockPageModel}
          getItemFromPageModel={getItemFromPageModel}
          respGridUtil={respGridUtil}
          ErrorHandlerAlert={ErrorHandlerAlert}
          flexLayout={true}
        />
      )}
    </>
  );
};
